import { useCallback } from 'react';

import { approveOAuthConsent, denyOAuthConsent } from 'src/graphql/oauthConsent';
import { navigateToUrl } from 'src/lib/browser';
// @ts-expect-error - no TypeScript definitions yet
import { useErrorHandler } from 'src/services/useErrorHandler';
import type { BackendOAuthConsentResult } from 'src/types/backend';

import { OAUTH_CONSENT_DECISION } from '../../lib';
import { useMainActions } from '../../store/main';

interface Handlers {
  handleAuthorize: () => Promise<void>;
  handleCancel: () => Promise<void>;
}

const navigateToResult = (result: BackendOAuthConsentResult | undefined): void => {
  if (!result) {
    throw new Error('The consent decision returned no redirect URL');
  }

  navigateToUrl(result.redirectUrl);
};

// The buttons stay busy after a decision succeeds, because the browser leaves the page.
export const useHandlers = (): Handlers => {
  const mainActions = useMainActions();
  const handleError = useErrorHandler();

  const handleAuthorize = useCallback(async () => {
    const workspaceId = mainActions.getSelectedWorkspaceId();
    if (!workspaceId) {
      return;
    }

    mainActions.setPendingDecision(OAUTH_CONSENT_DECISION.approve);
    try {
      navigateToResult(
        await approveOAuthConsent({ authorizeUrl: mainActions.getAuthorizeUrl(), workspaceId })
      );
    } catch (error) {
      mainActions.setPendingDecision(null);
      handleError(error);
    }
  }, [mainActions, handleError]);

  const handleCancel = useCallback(async () => {
    mainActions.setPendingDecision(OAUTH_CONSENT_DECISION.deny);
    try {
      navigateToResult(await denyOAuthConsent({ authorizeUrl: mainActions.getAuthorizeUrl() }));
    } catch (error) {
      mainActions.setPendingDecision(null);
      handleError(error);
    }
  }, [mainActions, handleError]);

  return { handleAuthorize, handleCancel };
};
